import React, { useRef, useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import ProjectSelector from '../components/ProjectSelector';
import { getMemberRateForMonth } from '../utils/costUtils';
import AllocationGrid from '../components/AllocationGrid';
import ProjectCosts from '../components/ProjectCosts';
import CostSummary from '../components/CostSummary';
import Charts from '../components/Charts';
import PermissionGuard from '../components/PermissionGuard';
import AccessDenied from '../components/AccessDenied';
import { Project, ProjectCost } from '../types/api';
import { AllocationMember } from '../types/allocation';
import { generateMonths } from '../utils/monthUtils';
import { DEFAULT_PROFIT_PERCENT, revenueFromProfitPercent, round2 } from '../utils/profitability';
import { DashboardService } from '../services/dashboardService';
import projectService from '../services/projectService';
import { MemberCostService, ProjectMemberService } from '../services';
import type { MemberCosts } from '../services/memberCostService';
import { useCallback } from 'react';

type TabType = 'allocation' | 'costs';

const DashboardPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('edit');
  
  // State for project selection and costs
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [projectCosts, setProjectCosts] = useState<ProjectCost[]>([]);
  const [allocationMembers, setAllocationMembers] = useState<AllocationMember[]>([]);
  const [memberCosts, setMemberCosts] = useState<MemberCosts>({});
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editingDashboardId, setEditingDashboardId] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('allocation');
  // Quote margin and the resulting price; saved alongside the dashboard.
  const [profitability, setProfitability] = useState<{
    profitPercent: number;
    revenue: number | null;
  }>({ profitPercent: DEFAULT_PROFIT_PERCENT, revenue: null });
  const costFetchKeyRef = useRef<string>('');

  // Memoize the callback to prevent infinite loops
  const handleAllocationChange = useCallback((members: AllocationMember[]) => {
    setAllocationMembers(members);
  }, []); // Empty deps - function never changes

  // Handle project costs change from AllocationGrid
  const handleProjectCostsChange = useCallback((costs: ProjectCost[]) => {
    setProjectCosts(costs);
  }, []);

  // Reset project costs and allocations when project changes (unless in edit mode)
  useEffect(() => {
    if (selectedProject && !isEditing) {
      // Reset costs and allocations when a new project is selected
      // AllocationGrid will populate them from API if they exist
      setProjectCosts([]);
      setAllocationMembers([]);
    }
  }, [selectedProject?.id, isEditing]);

  // Load dashboard data if edit parameter is present
  useEffect(() => {
    if (editId) {
      loadDashboardForEdit(parseInt(editId));
    }
  }, [editId]);

  const loadDashboardForEdit = async (dashboardId: number) => {
    console.log("Loading dashboard for edit:", dashboardId);
    try {
      const data = await DashboardService.getDashboardById(dashboardId);
      if (!data) {
        throw new Error('Dashboard not found');
      }

      // Fetch full project details
      let projectDetails = await projectService.getProjectById(data.projectId);
      if (!projectDetails) {
        projectDetails = {
          id: data.projectId,
          name: data.projectName || `Project ${data.projectId}`,
          identifier: `project-${data.projectId}`,
          status: 1,
          is_public: 0,
          inherit_members: 0,
          created_on: new Date().toISOString(),
          updated_on: new Date().toISOString(),
        } as Project;
      }

      setSelectedProject(projectDetails);

      // Restore the saved quote. Older dashboards have neither field, so they
      // fall back to the default percent and a revenue derived from it.
      const savedCosts = data.calculatedCosts as (typeof data.calculatedCosts & {
        profitPercent?: number | null;
        revenue?: number | null;
      }) | undefined;
      setProfitability({
        profitPercent: savedCosts?.profitPercent ?? DEFAULT_PROFIT_PERCENT,
        revenue: savedCosts?.revenue ?? null,
      });

      // Load saved project costs (handle old and new format)
      if (data.projectCosts && Array.isArray(data.projectCosts)) {
        const monthsForConversion = generateMonths(new Date().toISOString().split('T')[0], 12);
        const convertedCosts = data.projectCosts.map((cost: any) => {
          if (cost.monthlyCost !== undefined && !cost.monthlyCosts) {
            return {
              ...cost,
              monthlyCosts: monthsForConversion.reduce((acc, month) => ({
                ...acc,
                [month.monthStart]: cost.monthlyCost || 0,
              }), {}),
            };
          }
          return cost;
        });
        setProjectCosts(convertedCosts);
      } else {
        setProjectCosts([]);
      }

      setAllocationMembers(data.allocationMembers || []);
      setIsEditing(true);
      setEditingDashboardId(dashboardId);
    } catch (err) {
      console.error('Error loading dashboard:', err);
      alert('Failed to load dashboard for editing');
    }
  };

  console.log('selectedProject', selectedProject);
  
  // Generate months for cost summary
  // In edit mode: Use the actual project duration or default to 12 months
  // In new mode: Default to 12 months from current date
  const months = selectedProject ? generateMonths(new Date().toISOString().split('T')[0], 12) : [];
  const monthKeySignature = useMemo(() => {
    if (!months.length) {
      return '';
    }
    return months.map(m => m.monthStart.substring(0, 7)).join(',');
  }, [months]);
  const memberIdsSignature = useMemo(() => {
    if (!allocationMembers.length) {
      return '';
    }
    const ids = allocationMembers.map(m => m.id).sort((a, b) => a - b);
    return ids.join(',');
  }, [allocationMembers]);
  const selectedProjectId = selectedProject?.id || null;

  // Fetch month-specific costs when allocationMembers change
  useEffect(() => {
    if (!selectedProjectId || !memberIdsSignature || !monthKeySignature) {
      if (!memberIdsSignature && costFetchKeyRef.current) {
        setMemberCosts({});
        costFetchKeyRef.current = '';
      }
      return;
    }

    const fetchKey = `${selectedProjectId}|${memberIdsSignature}|${monthKeySignature}`;
    if (fetchKey === costFetchKeyRef.current) {
      return;
    }
    costFetchKeyRef.current = fetchKey;

    const memberIds = memberIdsSignature.split(',').map(id => Number(id)).filter(id => !Number.isNaN(id));
    const monthKeys = monthKeySignature.split(',').filter(Boolean);

    if (!memberIds.length || !monthKeys.length) {
      return;
    }

    console.log('💰 Fetching month-specific costs for CostSummary...');
    MemberCostService.getMemberCostsByMonths(memberIds, monthKeys)
      .then(costs => {
        console.log('✅ Member costs loaded for CostSummary:', costs);
        setMemberCosts(costs);
      })
      .catch(error => {
        console.error('❌ Error loading member costs:', error);
        // Continue with default costs if fetch fails
      });
  }, [selectedProjectId, memberIdsSignature, monthKeySignature]);

  // Ref to capture the exportable content

  const handleSubmit = () => {
    // Here you can add any API calls or additional logic needed when changes are applied
    console.log('Project configuration submitted:', {
      project: selectedProject,
      projectCosts,
    });
    // You can add API calls here to load project members and costs
  };

  // Rate a member's hours are costed at - the TMIS employee cost rate
  const getMemberCostForMonth = (memberId: number, monthStart: string): number => {
    const member = allocationMembers.find(m => m.id === memberId);
    return getMemberRateForMonth(member, memberCosts, monthStart);
  };

  const calculateTotals = () => {
    // Calculate employee costs using month-specific rates
    const totalEmployeeCosts = allocationMembers.reduce((total, member) => {
      return total + months.reduce((memberTotal, month) => {
        const hours = member.monthlyAllocations[month.monthStart] || 0;
        const costForMonth = getMemberCostForMonth(member.id, month.monthStart);
        return memberTotal + (hours * costForMonth);
      }, 0);
    }, 0);

    // Calculate project costs (sum all monthly costs)
    const billableCosts = projectCosts.filter(cost => cost.billable)
      .reduce((total, cost) => {
        const costTotal = Object.values(cost.monthlyCosts).reduce((sum, val) => sum + (val || 0), 0);
        return total + costTotal;
      }, 0);
    const nonBillableCosts = projectCosts.filter(cost => !cost.billable)
      .reduce((total, cost) => {
        const costTotal = Object.values(cost.monthlyCosts).reduce((sum, val) => sum + (val || 0), 0);
        return total + costTotal;
      }, 0);
    const totalProjectCosts = billableCosts + nonBillableCosts;
    const totalCost = totalEmployeeCosts + totalProjectCosts;

    return {
      totalEmployeeCosts,
      totalProjectCosts,
      totalCost,
      billableCosts,
      nonBillableCosts
    };
  };

  const calculateMonthlyBreakdown = () => {
    return months.map(month => {
      // Calculate employee costs for this month using month-specific rates
      const employeeCosts = allocationMembers.reduce((total, member) => {
        const hours = member.monthlyAllocations[month.monthStart] || 0;
        const costForMonth = getMemberCostForMonth(member.id, month.monthStart);
        return total + (hours * costForMonth);
      }, 0);

      // Calculate project costs for this specific month
      const projectCostsForMonth = projectCosts.reduce((total, cost) => {
        return total + (cost.monthlyCosts[month.monthStart] || 0);
      }, 0);

      // Calculate total hours for this month
      const totalHours = allocationMembers.reduce((total, member) => {
        return total + (member.monthlyAllocations[month.monthStart] || 0);
      }, 0);

      return {
        monthStart: month.monthStart,
        monthLabel: month.label,
        employeeCosts,
        projectCosts: projectCostsForMonth,
        totalCost: employeeCosts + projectCostsForMonth,
        totalHours
      };
    });
  };

  const handleCancelEdit = () => {
    // Clear all state and exit edit mode
    setIsEditing(false);
    setEditingDashboardId(null);
    setSelectedProject(null);
    setProjectCosts([]);
    setAllocationMembers([]);
    
    // Navigate to clean dashboard page
    window.location.href = '/dashboard';
  };

  const handleSaveData = async () => {
    if (!selectedProject) {
      alert('Please select a project first');
      return;
    }

    setIsSaving(true);
    try {
      const baseCosts = calculateTotals();
      const calculatedCosts = {
        ...baseCosts,
        profitPercent: profitability.profitPercent,
        // Not yet seeded (e.g. saved before the summary rendered) - derive it.
        revenue:
          profitability.revenue ??
          round2(revenueFromProfitPercent(baseCosts.totalCost, profitability.profitPercent)),
      };
      const monthlyBreakdown = calculateMonthlyBreakdown();

      // Filter out invalid or empty members before saving
      const validAllocationMembers = allocationMembers
        .filter(member => 
          member.member && 
          member.member.id && 
          member.member.firstname && 
          member.member.lastname &&
          !member.removed
        )
        // Remove duplicates based on member ID
        .filter((member, index, arr) => 
          arr.findIndex(m => m.member.id === member.member.id) === index
        );

      // If editing a dashboard (WhatIf scenario), save to dashboard tables
      if (isEditing && editingDashboardId) {
        const dashboardData = {
          projectId: selectedProject.id,
          label: selectedProject.name,
          allocationMembers: validAllocationMembers,
          projectCosts,
          calculatedCosts,
          monthlyBreakdown,
          savedAt: new Date().toISOString()
        };

        const result = await DashboardService.saveDashboardData(dashboardData);
        alert(`Dashboard data saved successfully! Save ID: ${result.id}`);
        console.log('Dashboard data saved:', result);
      } else {
        // Save to pbs_projects_allocations for Project Budget
        // Convert allocationMembers to the format expected by the API
        const allocations = validAllocationMembers.map(member => {
          // Use member's per_hour_cost as hourly_rate
          const hourlyRate = member.member.per_hour_cost || 0;
          // Rate billed to the client - falls back to the member's own rate
          const clientHourlyRate = member.clientHourlyRate !== undefined && member.clientHourlyRate !== null
            ? member.clientHourlyRate
            : hourlyRate;

          return {
            user_id: member.member.id,
            monthlyAllocations: member.monthlyAllocations || {},
            hourly_rate: hourlyRate,
            client_hourly_rate: clientHourlyRate
          };
        });

        // Prepare project budget data
        // Use totalCost as totalBudget (calculated cost becomes the budget)
        const projectBudget = {
          totalBudget: calculatedCosts.totalCost || 0,
          calculatedCosts: calculatedCosts,
          monthlyBreakdown: monthlyBreakdown
        };

        // Convert projectCosts to the format expected by the API
        const costsForAPI = projectCosts.map(cost => ({
          category: cost.id,
          description: cost.description,
          billable: cost.billable,
          monthlyCosts: cost.monthlyCosts || {}
        }));

        // Save to pbs_projects_allocations
        const result = await ProjectMemberService.saveProjectAllocations(
          selectedProject.id,
          allocations,
          projectBudget,
          costsForAPI,
          // The grid posted its full team, so added/removed members persist too
          true
        );

        if (result.success) {
          alert('Project budget saved successfully!');
          console.log('Project budget saved:', result);
        } else {
          throw new Error(result.message || 'Failed to save project budget');
        }
      }
      
    } catch (error) {
      console.error('Error saving data:', error);
      alert(`Failed to save data: ${error instanceof Error ? error.message : 'Please try again.'}`);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <PermissionGuard
      pbsRole="project_owner"
      fallback={
        <AccessDenied
          title="Project Owner Access Required"
          message="Only PBS-approved project owners can create or modify project dashboards."
        />
      }
    >
      <div className="space-y-6 bg-gradient-to-br from-white via-blue-50/40 to-indigo-50/30 rounded-xl p-6 shadow-sm border border-blue-100/50">
      {/* Editing Mode Banner */}
      {isEditing && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <svg className="h-5 w-5 text-blue-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              <div>
                <h3 className="text-sm font-medium text-blue-800">Editing Mode</h3>
                <p className="mt-1 text-sm text-blue-700">
                  You are editing Dashboard ID: {editingDashboardId}. Click "Update Dashboard" to save changes or "Cancel" to discard.
                </p>
              </div>
            </div>
            <button
              onClick={handleCancelEdit}
              aria-label="Cancel editing and discard changes"
              className="px-3 py-1.5 text-sm font-medium text-blue-700 hover:text-blue-800 hover:bg-blue-100 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              Cancel Edit
            </button>
          </div>
        </div>
      )}

      {/* Page Header */}
      <div className="border-b border-gray-200 pb-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {isEditing ? 'Edit Project Budgeting' : 'Project Budgeting'}
            </h2>
            <p className="mt-1 text-sm text-gray-600">Follow the steps below to create your project budget</p>
          </div>
        </div>
      </div>

      {/* Step-by-Step Flow Indicator */}
      <div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-sm border border-blue-100/50 p-6 mb-6">
        <div className="flex items-center justify-between">
          {/* Step 1: Select Project */}
          <div className="flex items-center flex-1">
            <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
              selectedProject 
                ? 'bg-green-500 border-green-500 text-white' 
                : 'bg-blue-100 border-blue-300 text-blue-600'
            } font-semibold`}>
              {selectedProject ? (
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                '1'
              )}
            </div>
            <div className="ml-4 flex-1">
              <p className={`text-sm font-medium ${selectedProject ? 'text-green-700' : 'text-gray-700'}`}>
                Step 1: Select Project
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                {selectedProject ? `Selected: ${selectedProject.name}` : 'Choose a project to begin'}
              </p>
            </div>
          </div>

          {/* Connector Line */}
          <div className={`flex-1 h-0.5 mx-4 ${selectedProject ? 'bg-green-500' : 'bg-gray-300'}`}></div>

          {/* Step 2: Assign Hours */}
          <div className="flex items-center flex-1">
            <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
              selectedProject && allocationMembers.length > 0
                ? 'bg-green-500 border-green-500 text-white'
                : selectedProject
                ? 'bg-blue-100 border-blue-300 text-blue-600'
                : 'bg-gray-100 border-gray-300 text-gray-400'
            } font-semibold`}>
              {selectedProject && allocationMembers.length > 0 ? (
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                '2'
              )}
            </div>
            <div className="ml-4 flex-1">
              <p className={`text-sm font-medium ${
                selectedProject && allocationMembers.length > 0
                  ? 'text-green-700'
                  : selectedProject
                  ? 'text-gray-700'
                  : 'text-gray-400'
              }`}>
                Step 2: Assign Hours
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                {selectedProject && allocationMembers.length > 0
                  ? `${allocationMembers.length} members configured`
                  : selectedProject
                  ? 'Assign hours to team members'
                  : 'Complete step 1 first'}
              </p>
            </div>
          </div>

          {/* Connector Line */}
          <div className={`flex-1 h-0.5 mx-4 ${
            selectedProject && allocationMembers.length > 0 ? 'bg-green-500' : 'bg-gray-300'
          }`}></div>

          {/* Step 3: Update Costs */}
          <div className="flex items-center flex-1">
            <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
              selectedProject && allocationMembers.length > 0 && projectCosts.length > 0
                ? 'bg-green-500 border-green-500 text-white'
                : selectedProject && allocationMembers.length > 0
                ? 'bg-blue-100 border-blue-300 text-blue-600'
                : 'bg-gray-100 border-gray-300 text-gray-400'
            } font-semibold`}>
              {selectedProject && allocationMembers.length > 0 && projectCosts.length > 0 ? (
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                '3'
              )}
            </div>
            <div className="ml-4 flex-1">
              <p className={`text-sm font-medium ${
                selectedProject && allocationMembers.length > 0 && projectCosts.length > 0
                  ? 'text-green-700'
                  : selectedProject && allocationMembers.length > 0
                  ? 'text-gray-700'
                  : 'text-gray-400'
              }`}>
                Step 3: Update Costs
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                {selectedProject && allocationMembers.length > 0
                  ? 'Add project expenses'
                  : 'Complete step 2 first'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Project Selection Component */}
      {isEditing && selectedProject ? (
        <div className="bg-white rounded-lg shadow-sm border border-blue-100 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Project locked during edit</p>
              <h3 className="text-lg font-semibold text-gray-900">{selectedProject.name}</h3>
              <p className="text-sm text-gray-500">
                ID: {selectedProject.id} • Identifier: {selectedProject.identifier || 'N/A'}
              </p>
            </div>
            <div className="text-sm text-blue-700 font-medium">
              Create a new dashboard to change the project.
            </div>
          </div>
        </div>
      ) : (
        <ProjectSelector
          selectedProject={selectedProject}
          onProjectChange={setSelectedProject}
          onSubmit={handleSubmit}
        />
      )}


      {/* Tabbed Content - Only show when project is selected */}
      {selectedProject && (
        <div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-sm border border-blue-100/50 overflow-hidden">
          {/* Tab Navigation */}
          <div className="border-b border-gray-200 bg-gray-50/50">
            <nav className="flex -mb-px" aria-label="Tabs">
              <button
                onClick={() => setActiveTab('allocation')}
                className={`
                  flex-1 px-6 py-4 text-sm font-medium text-center border-b-2 transition-colors
                  ${activeTab === 'allocation'
                    ? 'border-blue-500 text-blue-600 bg-blue-50'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                  }
                `}
                aria-label="Team Allocation tab"
                aria-selected={activeTab === 'allocation'}
                role="tab"
              >
                <div className="flex items-center justify-center space-x-2">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  <span>Step 2: Team Allocation</span>
                  {allocationMembers.length > 0 && (
                    <span className="ml-2 px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                      {allocationMembers.length}
                    </span>
                  )}
                </div>
              </button>
              <button
                onClick={() => setActiveTab('costs')}
                className={`
                  flex-1 px-6 py-4 text-sm font-medium text-center border-b-2 transition-colors
                  ${activeTab === 'costs'
                    ? 'border-blue-500 text-blue-600 bg-blue-50'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                  }
                `}
                aria-label="Project Costs tab"
                aria-selected={activeTab === 'costs'}
                role="tab"
              >
                <div className="flex items-center justify-center space-x-2">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>Step 3: Project Costs</span>
                  {projectCosts.length > 0 && (
                    <span className="ml-2 px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                      {projectCosts.length}
                    </span>
                  )}
                </div>
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'allocation' && (
              <div className="space-y-4">
                <div className="mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Assign Hours to Team Members</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Project: <span className="font-medium">{selectedProject.name}</span> (ID: {selectedProject.id})
                  </p>
                  <p className="text-xs text-gray-500 mt-2">
                    💡 Enter the number of hours each team member will work per month. Maximum 160 hours per month.
                  </p>
                </div>
                <AllocationGrid
                  selectedProject={selectedProject}
                  onAllocationChange={handleAllocationChange}
                  onProjectCostsChange={handleProjectCostsChange}
                  initialMembers={isEditing ? allocationMembers : undefined}
                />
              </div>
            )}

            {activeTab === 'costs' && (
              <div className="space-y-4">
                <div className="mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Add Project Expenses</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Enter additional project costs such as travel, software licenses, hardware, etc.
                  </p>
                  <p className="text-xs text-gray-500 mt-2">
                    💡 Mark costs as billable if they should be included in client billing.
                  </p>
                </div>
                <ProjectCosts
                  costs={projectCosts}
                  months={months}
                  onCostsChange={setProjectCosts}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Cost Summary Component */}
      {selectedProject && allocationMembers.length > 0 && (
        <CostSummary
          allocationMembers={allocationMembers}
          months={months}
          projectCosts={projectCosts}
          memberCosts={memberCosts}
          endDate={selectedProject.updated_on || ''}
          profitPercent={profitability.profitPercent}
          revenue={profitability.revenue}
          onProfitabilityChange={setProfitability}
        />
      )}

      {/* Charts Component */}
      {selectedProject && allocationMembers.length > 0 && (
        <Charts
          allocationMembers={allocationMembers}
          months={months}
          projectCosts={projectCosts}
          endDate={selectedProject.updated_on || ''}
        />
      )}

      {!selectedProject && (
        <div className="bg-white/80 backdrop-blur-sm rounded-lg shadow p-12 border border-blue-100/50">
          <div className="text-center py-8">
            <div className="mx-auto h-16 w-16 text-blue-500 mb-4">
              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Start by Selecting a Project</h3>
            <p className="text-sm text-gray-600 max-w-md mx-auto">
              Choose a project from the list above to begin creating your budget. You'll then assign hours to team members and add project costs.
            </p>
            <div className="mt-6 flex items-center justify-center space-x-6 text-xs text-gray-500">
              <div className="flex items-center">
                <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 font-semibold flex items-center justify-center mr-2">1</span>
                <span>Select Project</span>
              </div>
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              <div className="flex items-center">
                <span className="w-6 h-6 rounded-full bg-gray-100 text-gray-400 font-semibold flex items-center justify-center mr-2">2</span>
                <span>Assign Hours</span>
              </div>
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              <div className="flex items-center">
                <span className="w-6 h-6 rounded-full bg-gray-100 text-gray-400 font-semibold flex items-center justify-center mr-2">3</span>
                <span>Add Costs</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sticky Save/Cancel Footer */}
      {selectedProject && allocationMembers.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                {isEditing ? (
                  <span className="flex items-center">
                    <svg className="h-4 w-4 text-blue-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    Editing Dashboard ID: {editingDashboardId}
                  </span>
                ) : (
                  <span>Ready to save dashboard</span>
                )}
              </div>
              <div className="flex items-center gap-3">
                {isEditing && (
                  <button
                    onClick={handleCancelEdit}
                    aria-label="Cancel editing and discard changes"
                    className="px-6 py-2.5 bg-white text-gray-700 text-sm font-medium rounded-md border border-gray-300 hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                  >
                    Cancel
                  </button>
                )}
                <button
                  onClick={handleSaveData}
                  disabled={isSaving}
                  aria-label={isSaving ? "Saving dashboard, please wait" : isEditing ? "Save dashboard updates" : "Save project budget"}
                  aria-busy={isSaving}
                  className="px-6 py-2.5 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed flex items-center gap-2 shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-all"
                >
                  {isSaving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      {isEditing ? 'Saving...' : 'Saving...'}
                    </>
                  ) : (
                    <>
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      {isEditing ? 'Save Updates' : 'Save Budget'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add padding at bottom to prevent content from being hidden by sticky footer */}
      {selectedProject && allocationMembers.length > 0 && (
        <div className="h-20"></div>
      )}
    </div>
    </PermissionGuard>
  );
};

export default DashboardPage;
